import type { components } from '@connected/api-client/api.generated';
import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validatorsv2.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import { ClearFields, SingleCheckOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Input } from '@connected/ui/components/input';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ElectricalProjectImage } from '@/components/electrical-project-image';
import { ObservationComponent } from '@/components/features/observation';
import { useInverter } from '@/hooks/queries/use-inverters';
import { useElectricalProjectFileDelete } from '@/hooks/use-electrical-project-file-delete';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/pv-inverters/$inverterId/',
)({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
});

function Page() {
    return (
        <div className="flex flex-col gap-4">
            <GeneralInformation />
            <Measurements />
            <Strings />
        </div>
    );
}

type GeneralInformationFormValues = {
    identifier: string;
    brandAndModel: string;
    serialNumber: string;
    firmwareVersion: string;
    electricalProjectDistributionBoardId: string | null;
    ratedPower: number | null;
    electricalProjectFiles: components['schemas']['ListElectricalProjectPVInverterQueryResultFile'][];
};

function GeneralInformation() {
    const { t } = useTranslation('re-inspections');
    const params = Route.useParams();

    const { data } = useInverter({
        pvInverterId: params.inverterId,
        electricalProjectVersionId: params.projectVersionId,
    });

    const { data: distributionBoardsData } = useQuery(
        elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }),
    );

    const defaultValues: GeneralInformationFormValues = {
        identifier: data?.inverter?.Identifier || '',
        brandAndModel: data?.inverter?.BrandModel || '',
        serialNumber: data?.inverter?.SerialNumber || '',
        firmwareVersion: data?.inverter?.FirmwareVersion || '',
        ratedPower: data?.inverter?.RatedPower || null,
        electricalProjectFiles: data?.inverter?.Files?.filter(file => file.Kind === 'PVInverterImage' && file.ElectricalProjectFileId) || [],
        electricalProjectDistributionBoardId: data?.inverter?.ElectricalProjectDistributionBoardId || '',
    };

    const { Field } = useForm<GeneralInformationFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
    });

    const distributionBoardOptions = useMemo(() => {
        return distributionBoardsData?.DistributionBoards?.map(db => ({
            label: db.Identifier || t('pv-inverter.unnamedDistributionBoard'),
            value: db.ElectricalProjectDistributionBoardId || '',
        })) || [];
    }, [distributionBoardsData?.DistributionBoards, t]);

    const { patchField } = usePatcher('/api/v2/electrical/projects/pvinverters/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            const inverterQueryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                electricalProjectVersionId: params.projectVersionId,
            });

            await queryClient.cancelQueries(inverterQueryOptions);

            const previousInverterData = queryClient.getQueryData(inverterQueryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'];
            const newInverterListData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                ...previousInverterData,
                Inverters: previousInverterData.Inverters.map((inverter) => {
                    if (inverter.ElectricalProjectPVInverterId === params.inverterId) {
                        return {
                            ...inverter,
                            ...body,
                        };
                    }
                    return inverter;
                }),
            };

            logger.debug({
                message: 'onMutate(inverterQueryOptions)',
                path: '/api/v2/electrical/projects/pvinverters/patch',
                inverters: {
                    previousData: previousInverterData,
                    newData: newInverterListData,
                    queryKey: inverterQueryOptions.queryKey,
                },
            });

            queryClient.setQueryData(inverterQueryOptions.queryKey, newInverterListData);

            return {
                previousData: previousInverterData,
                queryKey: inverterQueryOptions.queryKey,
            };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectPVInverterId: params.inverterId,
        },
    });

    return (
        <Section>
            <Section.Header>
                <Section.Header.Title>Algemene informatie</Section.Header.Title>
            </Section.Header>
            <Section.Content>
                <div className="grid grid-cols-2 gap-4">
                    <Field
                        name="identifier"
                        validators={{
                            onChange:
                            validators.editElectricalProjectPVInverterCommand.identifier,
                            onChangeAsync: patchField('identifier'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('pv-inverter.form.identifier.label')}
                            >
                                <Input
                                    name={name}
                                    onChange={e =>
                                        handleChange(e.target.value)}
                                    value={state.value}
                                    placeholder={t('pv-inverter.form.identifier.placeholder')}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="brandAndModel"
                        validators={{
                            onChange:
                            validators.editElectricalProjectPVInverterCommand.brandModel,
                            onChangeAsync: patchField('brandModel'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('pv-inverter.form.brandAndModel.label')}
                            >
                                <Input
                                    name={name}
                                    onChange={e =>
                                        handleChange(e.target.value)}
                                    value={state.value}
                                    placeholder={t('pv-inverter.form.brandAndModel.placeholder')}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="serialNumber"
                        validators={{
                            onChange:
                            validators.editElectricalProjectPVInverterCommand.serialNumber,
                            onChangeAsync: patchField('serialNumber'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('pv-inverter.form.serialNumber.label')}
                            >
                                <Input
                                    name={name}
                                    onChange={e =>
                                        handleChange(e.target.value)}
                                    value={state.value}
                                    placeholder={t('pv-inverter.form.serialNumber.placeholder')}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="firmwareVersion"
                        validators={{
                            onChange:
                            validators.editElectricalProjectPVInverterCommand.firmwareVersion,
                            onChangeAsync: patchField('firmwareVersion'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('pv-inverter.form.firmwareVersion.label')}
                            >
                                <Input
                                    name={name}
                                    onChange={e =>
                                        handleChange(e.target.value)}
                                    value={state.value}
                                    placeholder={t('pv-inverter.form.firmwareVersion.placeholder')}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                </div>
                <Field
                    name="electricalProjectDistributionBoardId"
                    validators={{
                        onChange:
                        validators.editElectricalProjectFinalCircuitCommand.electricalProjectDistributionBoardId,
                        onChangeAsync: patchField('electricalProjectDistributionBoardId'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('pv-inverter.form.parentDistributionBoard.label')}
                            className="relative"
                        >
                            <ClearFields onClear={() => handleChange(null)} />
                            <SingleOptionField
                                name={name}
                                handleChange={handleChange}
                                state={state}
                                options={distributionBoardOptions}
                                render={
                                    SingleCheckOptionItem
                                }
                                dropdown
                                placeholder={t('pv-inverter.form.parentDistributionBoard.placeholder')}
                                emptyOptionsMessage={t('pv-inverter.form.parentDistributionBoard.empty')}
                                closeOnClick
                            />
                        </Label>
                    )}
                </Field>
                <Field
                    name="ratedPower"
                    validators={{
                        onChange:
                        validators.editElectricalProjectPVInverterCommand.ratedPower,
                        onChangeAsync: patchField('ratedPower'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('pv-inverter.form.nominalPower.label')}
                            className="col-span-2"
                        >
                            <NumberField
                                name={name}
                                onChange={handleChange}
                                value={state.value}
                                placeholder={t('pv-inverter.form.nominalPower.placeholder')}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
                <FileField type="PVInverterImage" />
                <FileField type="PVInverterThermalImage" />
                <ObservationComponent
                    electricalProjectVersionId={params.projectVersionId}
                    objectId={params.inverterId!}
                    objectKind="PVInverter"
                />
            </Section.Content>
        </Section>

    );
}

const FileKindTexts: Record<components['schemas']['ElectricalPVInverterFileKind'], string> = {
    PVInverterImage: 'Overzichtsfoto\'s',
    PVStringTable: 'String referentie tabel',
    PVInverterThermalImage: 'Thermografische foto\'s',
};

function FileField({ type }: { type: components['schemas']['ElectricalPVInverterFileKind'] }) {
    const params = Route.useParams();

    const { data } = useInverter({
        pvInverterId: params.inverterId,
        electricalProjectVersionId: params.projectVersionId,
    });

    const fileDelete = useElectricalProjectFileDelete({
        mutationOptions: {
            onMutate: async (electricalProjectFileId) => {
                const electricalProjectPVInverterFileId = data.inverter?.Files?.find(file => file.ElectricalProjectFileId === electricalProjectFileId)?.ElectricalProjectPVInverterFileId;

                if (!electricalProjectPVInverterFileId) {
                    logger.warn({
                        system: 'PVInverter.fileDelete.onMutate',
                        message: 'ElectricalProjectPVInverterFileId not found',
                        electricalProjectFileId,
                    });

                    return;
                }

                const inverterQueryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId });

                await queryClient.cancelQueries(inverterQueryOptions);

                const previousInverterData = queryClient.getQueryData(inverterQueryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'];
                const newInverterListData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                    ...previousInverterData,
                    Inverters: previousInverterData.Inverters.map((inverter) => {
                        if (inverter.ElectricalProjectPVInverterId === params.inverterId) {
                            return {
                                ...inverter,
                                Files: inverter.Files?.filter(file => file.ElectricalProjectPVInverterFileId !== electricalProjectPVInverterFileId),
                            };
                        }
                        return inverter;
                    }),
                };

                queryClient.setQueryData(inverterQueryOptions.queryKey, newInverterListData);
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId }));
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    const fileLinkMutation = api.useMutation('post', '/api/v2/electrical/projects/pvinverters/files/create', {
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        scope: {
            id: params.projectVersionId,
        },
    });
    const fileUnlinkMutation = api.useMutation('delete', '/api/v2/electrical/projects/pvinverters/files/delete', {
        onMutate: async (variables) => {
            const { params } = variables;

            const electricalProjectFileId = data?.inverter?.Files?.find(file => file.ElectricalProjectPVInverterFileId === params.query.ElectricalProjectPVInverterFileId)?.ElectricalProjectFileId;

            if (!electricalProjectFileId) {
                logger.warn({
                    system: 'PVInverter.fileUnlinkMutation.onMutate',
                    message: 'ElectricalProjectFileId not found',
                    variables,
                });

                return;
            }

            fileDelete.delete(electricalProjectFileId);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
        scope: {
            id: params.projectVersionId,
        },
    });

    const fileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/pvinverter',
        mutationOptions: {
            onMutate: async (file) => {
                const electricalProjectPVInverterFileId = crypto.randomUUID();

                fileLinkMutation.mutate({
                    body: {
                        ElectricalProjectPVInverterId: params.inverterId,
                        ElectricalProjectPVInverterFileId: electricalProjectPVInverterFileId,
                        ElectricalProjectFileId: file.ElectricalProjectFileId!,
                        Kind: type,
                    },
                });

                const inverterQueryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({ electricalProjectVersionId: params.projectVersionId });

                const previousInverterData = queryClient.getQueryData(inverterQueryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'];
                const newInverterListData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                    ...previousInverterData,
                    Inverters: previousInverterData.Inverters.map((inverter) => {
                        if (inverter.ElectricalProjectPVInverterId === params.inverterId) {
                            return {
                                ...inverter,
                                Files: [
                                    ...(inverter.Files || []),
                                    {
                                        ElectricalProjectPVInverterFileId: electricalProjectPVInverterFileId,
                                        ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                        Kind: type,
                                        IsDeleted: false,
                                        LastUpdateDate: new Date().toISOString(),
                                    },
                                ],
                            };
                        }
                        return inverter;
                    }),
                };

                queryClient.setQueryData(inverterQueryOptions.queryKey, newInverterListData);
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    });

    return (
        <>
            <Label label={FileKindTexts[type]} className="col-span-2">
                <FileInput
                    accept={fileUpload.accept ?? undefined}
                    onChange={(files) => {
                        const file = files[0];

                        if (!file)
                            return;

                        fileUpload.upload({ file });
                    }}
                    mode="both"
                />
            </Label>
            <div className="flex flex-col gap-2 col-span-2">
                {data?.inverter?.Files?.filter(file => file.Kind === type).map(file => (
                    <div key={file.ElectricalProjectPVInverterFileId!} className="relative flex flex-row gap-2">
                        <ElectricalProjectImage
                            className="h-[112px] w-full rounded-lg"
                            query={{
                                ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                ImageQuality: 'Medium',
                            }}
                            expandable={true}
                            alt=""
                        />
                        <Button
                            className="absolute top-1 right-1 bg-background/50"
                            variant="ghost"
                            size="icon"
                            onClick={() => fileUnlinkMutation.mutate({
                                params: {
                                    query: {
                                        ElectricalProjectPVInverterFileId: file?.ElectricalProjectPVInverterFileId ?? '',
                                    },
                                },
                            })}
                            disabled={fileDelete.isDisabled || !file?.ElectricalProjectPVInverterFileId || fileUnlinkMutation.isPending}
                        >
                            {fileUnlinkMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </Button>
                    </div>
                ))}
            </div>
        </>
    );
}

type MeasurementsFormValues = {
    groundImpedanceL1: number | null;
    groundImpedanceL2: number | null;
    groundImpedanceL3: number | null;
    neutralImpedanceL1: number | null;
    neutralImpedanceL2: number | null;
    neutralImpedanceL3: number | null;
    impedanceL1L2: number | null;
    impedanceL1L3: number | null;
    impedanceL2L3: number | null;
};

function Measurements() {
    const { t } = useTranslation('re-inspections');
    const params = Route.useParams();

    const { data } = useInverter({
        pvInverterId: params.inverterId,
        electricalProjectVersionId: params.projectVersionId,
    });

    const defaultValues: MeasurementsFormValues = {
        groundImpedanceL1: data?.inverter?.GroundImpedanceL1 || null,
        groundImpedanceL2: data?.inverter?.GroundImpedanceL2 || null,
        groundImpedanceL3: data?.inverter?.GroundImpedanceL3 || null,
        neutralImpedanceL1: data?.inverter?.NeutralImpedanceL1 || null,
        neutralImpedanceL2: data?.inverter?.NeutralImpedanceL2 || null,
        neutralImpedanceL3: data?.inverter?.NeutralImpedanceL3 || null,
        impedanceL1L2: data?.inverter?.ImpedanceL1L2 || null,
        impedanceL1L3: data?.inverter?.ImpedanceL1L3 || null,
        impedanceL2L3: data?.inverter?.ImpedanceL2L3 || null,
    };

    const { Field } = useForm<MeasurementsFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
    });

    const { patchField } = usePatcher('/api/v2/electrical/projects/pvinverters/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            const inverterQueryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                electricalProjectVersionId: params.projectVersionId,
            });

            await queryClient.cancelQueries(inverterQueryOptions);

            const previousInverterData = queryClient.getQueryData(inverterQueryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'];
            const newInverterListData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                ...previousInverterData,
                Inverters: previousInverterData.Inverters.map((inverter) => {
                    if (inverter.ElectricalProjectPVInverterId === params.inverterId) {
                        return {
                            ...inverter,
                            ...body,
                        };
                    }
                    return inverter;
                }),
            };

            logger.debug({
                message: 'onMutate(inverterQueryOptions)',
                path: '/api/v2/electrical/projects/pvinverters/patch',
                inverters: {
                    previousData: previousInverterData,
                    newData: newInverterListData,
                    queryKey: inverterQueryOptions.queryKey,
                },
            });

            queryClient.setQueryData(inverterQueryOptions.queryKey, newInverterListData);

            return {
                previousData: previousInverterData,
                queryKey: inverterQueryOptions.queryKey,
            };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectPVInverterId: params.inverterId,
        },
    });

    return (
        <Container>
            <Container.Header>
                <Container.Title>
                    {t('pv-inverter.measurementsTitle')}
                </Container.Title>
            </Container.Header>
            <Container.Content>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-4">
                        <Field
                            name="groundImpedanceL1"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.groundImpedanceL1,
                                onChangeAsync: patchField('groundImpedanceL1'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L1_PE.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L1_PE.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="groundImpedanceL2"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.groundImpedanceL2,
                                onChangeAsync: patchField('groundImpedanceL2'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L2_PE.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L2_PE.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="groundImpedanceL3"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.groundImpedanceL3,
                                onChangeAsync: patchField('groundImpedanceL3'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L3_PE.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L3_PE.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="impedanceL1L2"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.impedanceL1L2,
                                onChangeAsync: patchField('impedanceL1L2'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L1_L2.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L1_L2.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="impedanceL1L3"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.impedanceL1L3,
                                onChangeAsync: patchField('impedanceL1L3'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L1_L3.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L1_L3.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="impedanceL2L3"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.impedanceL2L3,
                                onChangeAsync: patchField('impedanceL2L3'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L2_L3.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L2_L3.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </div>
                    <div className="flex flex-col gap-4">
                        <Field
                            name="neutralImpedanceL1"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.neutralImpedanceL1,
                                onChangeAsync: patchField('neutralImpedanceL1'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L1_N.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L1_N.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="neutralImpedanceL2"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.neutralImpedanceL2,
                                onChangeAsync: patchField('neutralImpedanceL2'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L2_N.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L2_N.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                        <Field
                            name="neutralImpedanceL3"
                            validators={{
                                onChange:
                                validators.patchElectricalProjectPVInverterCommand.neutralImpedanceL3,
                                onChangeAsync: patchField('neutralImpedanceL3'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <Label
                                    className="grid grid-cols-3 items-center"
                                    label={t('pv-inverter.measurements.L3_N.label')}
                                >
                                    <NumberField
                                        className="col-span-2 w-full"
                                        maxPrecision={2}
                                        name={name}
                                        onChange={handleChange}
                                        value={state.value}
                                        placeholder={t('pv-inverter.measurements.L3_N.placeholder')}
                                    />
                                    <FieldInfo state={state} />
                                </Label>
                            )}
                        </Field>
                    </div>
                </div>
            </Container.Content>
        </Container>

    );
}

type FormValues = {
    identifier: string;
    voltage: number | null;
    resistance: number | null;
    current: number | null;
};

type DataTableColumn = {
    id: keyof FormValues | 'action';
    header: string;
};
// eslint-disable-next-line react-refresh/only-export-components
export function getColumns(t: (key: string) => string): DataTableColumn[] {
    return [
        { id: 'identifier', header: t('pv-inverter.table.columns.name') },
        { id: 'voltage', header: t('pv-inverter.table.columns.voltage') },
        { id: 'resistance', header: t('pv-inverter.table.columns.resistance') },
        { id: 'current', header: t('pv-inverter.table.columns.current') },
        { id: 'action', header: '' },
    ];
}

type DataTableProps<TData extends components['schemas']['ListElectricalProjectPVInverterQueryResultString']> = {
    columns: DataTableColumn[];
    data: TData[];
};

type DataTableRowProps = {
    row: components['schemas']['ListElectricalProjectPVInverterQueryResultString'];
    index: number;
    form: FormApi<SubFormValues, ZodValidator> & ReactFormApi<SubFormValues, ZodValidator>;
};

type SubFormValues = {
    strings: FormValues[];
};

export function DataTable<TData extends components['schemas']['ListElectricalProjectPVInverterQueryResultString']>({
    columns,
    data,
}: DataTableProps<TData>) {
    const { t } = useTranslation('re-inspections');
    const form = useForm<SubFormValues, ZodValidator>({
        defaultValues: {
            strings: [
                ...(data?.map(string => ({
                    id: string.ElectricalProjectPVStringId,
                    identifier: string.Identifier || '',
                    voltage: string.Voltage || null,
                    resistance: string.Resistance || null,
                    current: string.Current || null,
                })) || []),
            ],
        },
        validatorAdapter: zodValidator(),
    });

    return (
        data?.length === 0 && (
            <div className="text-center ">
                {t('pv-inverter.strings.empty')}
            </div>
        )
    ) || (
        <table className="w-full">
            {/* <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: '28%' }} />
                <col style={{ width: '28%' }} />
                <col style={{ width: '28%' }} />
                <col style={{ width: '4%' }} />
            </colgroup> */}
            <thead>
                <tr>
                    {columns.map(header => (
                        <th key={header.id} className="text-start px-1">
                            {header.header}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data?.map((row, index) => (
                    <DataTableRow key={row.ElectricalProjectPVStringId} row={row} index={index} form={form} />
                ))}
            </tbody>
        </table>
    );
}

function DataTableRow({ row, index, form }: DataTableRowProps) {
    const { t: ri } = useTranslation('re-inspections');
    const params = Route.useParams();

    const deleteDeviceMutation = api.useMutation('delete', '/api/v2/electrical/projects/pvinverters/strings/delete', {
        onMutate: async (variables) => {
            const queryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                electricalProjectVersionId: params.projectVersionId,
            });

            await queryClient.cancelQueries(queryOptions);

            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'] || [];
            const newData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                ...previousData,
                Inverters: previousData.Inverters.map(inverter => ({
                    ...inverter,
                    Strings: inverter?.Strings?.filter(string => string.ElectricalProjectPVStringId !== variables.params.query.ElectricalProjectPVStringId),
                })),
            };

            logger.debug({
                message: 'onMutate(deleteDeviceMutation)',
                data: {
                    previousData,
                    newData,
                    queryKey: queryOptions.queryKey,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return {
                previousData,
                queryKey: queryOptions.queryKey,
            };
        },
        onSettled: async (_data, _variables) => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                electricalProjectVersionId: params.projectVersionId,
            }));
        },
        scope: {
            id: params.projectVersionId,
        },
    });

    const patcher = usePatcher('/api/v2/electrical/projects/pvinverters/strings/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);
            const queryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                electricalProjectVersionId: params.projectVersionId,
            });

            await queryClient.cancelQueries(queryOptions);

            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'] || [];
            const newData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                ...previousData,
                Inverters: previousData.Inverters.map((inverter) => {
                    if (inverter.ElectricalProjectPVInverterId === params.inverterId) {
                        return {
                            ...inverter,
                            Strings: inverter.Strings?.map((string) => {
                                if (string.ElectricalProjectPVStringId === row.ElectricalProjectPVStringId) {
                                    return {
                                        ...string,
                                        ...body,
                                    };
                                }
                                return string;
                            }),
                        };
                    }
                    return inverter;
                }),
            };
            logger.debug({
                message: 'onMutate(patcher)',
                data: {
                    previousData,
                    newData,
                    queryKey: queryOptions.queryKey,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return {
                previousData,
                queryKey: queryOptions.queryKey,
            };
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectPVStringId: row!.ElectricalProjectPVStringId!,
        },
    });

    return (
        <tr key={row.ElectricalProjectPVStringId}>
            <td className="text-start px-1">
                <form.Field
                    name={`strings[${index}].identifier`}
                    validators={{
                        onChange: validators.patchElectricalProjectPVStringCommand.identifier,
                        onChangeAsync: patcher.patchField('Identifier'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <>
                            <Input
                                name={name}
                                onChange={e => handleChange(e.target.value)}
                                value={state.value}
                                placeholder="Noteer naam"
                                className="w-full"

                            />
                        </>
                    )}
                </form.Field>
            </td>
            <td className="text-start px-1">
                <form.Field
                    name={`strings[${index}].voltage`}
                    validators={{
                        onChange: validators.patchElectricalProjectPVStringCommand.voltage,
                        onChangeAsync: patcher.patchField('Voltage'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <NumberField
                            name={name}
                            onChange={handleChange}
                            value={state.value}
                            placeholder="Noteer spanning"
                            className="w-full"
                        />
                    )}
                </form.Field>
            </td>
            <td className="text-start px-1">
                <form.Field
                    name={`strings[${index}].resistance`}
                    validators={{
                        onChange: validators.patchElectricalProjectPVStringCommand.resistance,
                        onChangeAsync: patcher.patchField('Resistance'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <NumberField
                            name={name}
                            onChange={handleChange}
                            value={state.value}
                            placeholder="Noteer weerstand"
                            className="w-full"
                        />
                    )}
                </form.Field>
            </td>
            <td className="text-start px-1">
                <form.Field
                    name={`strings[${index}].current`}
                    validators={{
                        onChange: validators.patchElectricalProjectPVStringCommand.current,
                        onChangeAsync: patcher.patchField('Current'),
                        onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <NumberField
                            name={name}
                            onChange={handleChange}
                            value={state.value}
                            placeholder="Noteer stroom"
                            className="w-full"
                        />
                    )}
                </form.Field>
            </td>
            <td className="relative text-end px-1">
                <Button
                    className="w-full justify-start"
                    variant="destructive"
                    onClick={async () => {
                        if (await showConfirm(ri('pv-inverter.strings.messages.confirmDelete'))) {
                            deleteDeviceMutation.mutate({ params: { query: { ElectricalProjectPVStringId: row.ElectricalProjectPVStringId! } } });
                        }
                    }}
                >
                    <Trash2 size={16} />
                </Button>
            </td>
        </tr>
    );
}

function Strings() {
    const { t } = useTranslation('re-inspections');
    const params = Route.useParams();

    const { data: { inverter } } = useInverter({
        electricalProjectVersionId: params.projectVersionId,
        pvInverterId: params.inverterId,
    });

    const createStringMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/pvinverters/strings/create',
        {
            onMutate: async (variables) => {
                const body = toPascalCase(variables!.body!);

                const queryOptions = elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                    electricalProjectVersionId: params.projectVersionId,
                });

                await queryClient.cancelQueries(queryOptions);

                const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectPVInverterQueryResult'] || [];
                const newData: components['schemas']['ListElectricalProjectPVInverterQueryResult'] = {
                    ...previousData,
                    Inverters: previousData.Inverters.map((inverter) => {
                        if (inverter.ElectricalProjectPVInverterId === params.inverterId) {
                            return {
                                ...inverter,
                                Strings: [
                                    ...(inverter?.Strings || []),
                                    {
                                        ElectricalProjectPVStringId: body.ElectricalProjectPVStringId,
                                        Identifier: '',
                                        Voltage: null,
                                        Resistance: null,
                                        Current: null,
                                    },
                                ],
                            };
                        }
                        return inverter;
                    }),
                };

                logger.debug({
                    message: 'onMutate(createStringMutation)',
                    data: {
                        previousData,
                        newData,
                        queryKey: queryOptions.queryKey,
                    },
                });

                queryClient.setQueryData(queryOptions.queryKey, newData);

                return {
                    previousData,
                    queryKey: queryOptions.queryKey,
                };
            },
            onSettled: async (_data, _variables) => {
                await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
                    electricalProjectVersionId: params.projectVersionId,
                }));
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    async function handleNewString() {
        await createStringMutation.mutateAsync({
            body: {
                ElectricalProjectPVInverterId: params.inverterId,
                ElectricalProjectPVStringId: crypto.randomUUID(),
                Order: (inverter?.Strings?.length || 0) + 1,

            },
        });
    }

    return (
        <Section>
            <Section.Header>
                <Section.Header.Title>{t('pv-inverter.strings.title', { ns: 're-inspections' })}</Section.Header.Title>
                {/* <div className="flex flex-row items-center justify-center">
                    <Button variant="link" onClick={handleNewString}>{t('actions.general.add', { ns: 'common' })}</Button>
                </div> */}
                <Section.Header.Actions>
                    <Section.Header.Action
                        variant="default"
                        icon={<Plus />}
                        onClick={handleNewString}
                    />
                </Section.Header.Actions>
            </Section.Header>
            <Section.Content>
                <DataTable columns={getColumns(t)} data={inverter?.Strings || []} />
                <FileField type="PVStringTable" />
            </Section.Content>
        </Section>
    );
}
